export default ({ single, duration = 0, fullscreenOnTouch = false, init, ...props } = {}) => {
    let hlsjs = null; // imported hls.js script
    let hls = null; // hls instance
    let hlsSource = null; // original hls source, hls.js replaces video.src with its own
    let hlsRecoveries = 0; // fatal errors recovered since the last buffered fragment
    let animationFrame = null;

    return {
        ...props,

        currentTime: 0,
        bufferedTime: 0,
        paused: true,
        muted: false,
        isSeeking: false,
        isSeekingPaused: true,
        duration: 0,

        get progress() {
            return this.duration > 0 && Number.isFinite(this.duration)
                ? Math.max(0, Math.min(1, this.currentTime / this.duration))
                : 0;
        },

        get bufferedProgress() {
            return this.duration > 0 && Number.isFinite(this.duration)
                ? Math.max(0, Math.min(1, this.bufferedTime / this.duration))
                : 0;
        },

        init() {
            const video = this.$refs.video;

            this.muted = video.muted;
            this.paused = video.paused;
            this.duration =
                Number.isFinite(video.duration) && video.duration > 0
                    ? video.duration
                    : parseFloat(duration) || 0;

            this.currentTime = video.currentTime || 0;

            init?.call(this);
        },

        destroy() {
            this.stopProgressAnimation();
            hls?.destroy();
            hls = null;
        },

        startProgressAnimation() {
            if (animationFrame !== null) return;

            const update = () => {
                if (this.$refs.video.paused || this.$refs.video.ended) {
                    animationFrame = null;
                    return;
                }

                if (!this.isSeeking) {
                    this.currentTime = this.$refs.video.currentTime;
                }

                animationFrame = requestAnimationFrame(update);
            };

            animationFrame = requestAnimationFrame(update);
        },

        stopProgressAnimation() {
            if (animationFrame !== null) {
                cancelAnimationFrame(animationFrame);
                animationFrame = null;
            }
        },

        queryOthers() {
            if (single === true) {
                return document.querySelectorAll("video");
            }

            if (single) {
                return document.querySelectorAll(single);
            }

            return null;
        },

        play() {
            if (single) {
                this.queryOthers().forEach((video) => {
                    if (video !== this.$refs.video && !video.paused) {
                        video.pause();
                    }
                });
            }

            return this.withHls(() => this.$refs.video.play());
        },

        pause() {
            this.$refs.video.pause();
        },

        toggle() {
            return this.paused ? this.play() : this.pause();
        },

        replay() {
            this.pause();
            this.seek(0);
            return this.play();
        },

        mute() {
            this.$refs.video.muted = true;
            this.muted = true;
        },

        unmute() {
            this.$refs.video.muted = false;
            this.muted = false;
        },

        toggleMuted() {
            return this.muted ? this.unmute() : this.mute();
        },

        requestFullscreen() {
            document.querySelectorAll("video").forEach((video) => {
                if (video !== this.$refs.video) {
                    video.pause();
                }
            });

            const video = this.$refs.video;

            // iPhone only supports fullscreen through the native video player
            if (!video.requestFullscreen) {
                return video.webkitEnterFullscreen?.();
            }

            return video.requestFullscreen();
        },

        toggleFullScreen() {
            if (!document.fullscreenElement) {
                return this.requestFullscreen();
            }

            return document.exitFullscreen();
        },

        seek(seconds) {
            const video = this.$refs.video;
            const time = Number(seconds);

            if (!Number.isFinite(time)) return;

            const resume = !this.isSeekingPaused;

            this.isSeeking = false;
            this.currentTime = time;

            return this.withHls(() => {
                video.currentTime = time;

                if (resume) {
                    return this.play();
                }
            });
        },

        seeking(seconds) {
            if (!this.isSeeking) {
                this.isSeekingPaused = this.$refs.video.paused;
                this.isSeeking = true;
                this.pause();
            }

            const time = Number(seconds);

            if (Number.isFinite(time)) {
                this.currentTime = time;
            }
        },

        /**
         * Seeking (or hls.js) can leave several buffered ranges:
         * use the one containing the playback position, otherwise the furthest one
         */
        updateBufferedTime() {
            const video = this.$refs.video;
            const buffered = video.buffered;

            let bufferedTime = 0;

            for (let i = 0; i < buffered.length; i++) {
                if (
                    buffered.start(i) <= video.currentTime &&
                    buffered.end(i) >= video.currentTime
                ) {
                    bufferedTime = buffered.end(i);
                    break;
                }

                bufferedTime = Math.max(bufferedTime, buffered.end(i));
            }

            this.bufferedTime = bufferedTime;
        },

        formatTime(seconds) {
            const _seconds = Math.abs(seconds);

            const hours = Math.floor(_seconds / 3600);
            const minutes = Math.floor((_seconds % 3600) / 60);
            const secs = Math.floor(_seconds % 60);

            const fragments = hours ? [hours, minutes, secs] : [minutes, secs];

            const value = fragments.map((value) => value.toString().padStart(2, "0")).join(":");

            return seconds >= 0 ? value : `-${value}`;
        },

        progressTranslate(progress) {
            return (
                "translateX(calc(-100% + " +
                progress * 100 +
                "cqw + " +
                0.5 * Math.sin(Math.PI * progress) +
                "rem))"
            );
        },

        isHls(source) {
            const src = source ?? hlsSource ?? this.$refs.video.src;

            return src?.split("?")[0].endsWith(".m3u8");
        },

        /**
         * First import hls.js dependency
         */
        attachHls() {
            hlsSource ??= this.$refs.video.src;

            hlsjs ??= import("hls.js")
                .then(({ default: Hls }) => {
                    if (hls) return;
                    if (!Hls.isSupported()) return;

                    hls = new Hls({
                        maxBufferLength: 10,
                        maxMaxBufferLength: 20,
                        maxBufferSize: 2 * 1_000 * 1_000,
                        autoStartLoad: false,
                        capLevelToPlayerSize: true,
                    });

                    hls.on(Hls.Events.FRAG_BUFFERED, () => {
                        hlsRecoveries = 0;
                    });

                    hls.on(Hls.Events.ERROR, (event, { fatal, type }) => {
                        if (!fatal) return;

                        if (hlsRecoveries++ < 3) {
                            // a failed manifest has no levels to resume from
                            if (type === Hls.ErrorTypes.NETWORK_ERROR && hls.levels?.length) {
                                return hls.startLoad();
                            }

                            if (type === Hls.ErrorTypes.MEDIA_ERROR) {
                                return hls.recoverMediaError();
                            }
                        }

                        // unrecoverable: start over with a new instance on the next play
                        hls.destroy();
                        hls = null;
                        hlsjs = null;
                        hlsRecoveries = 0;
                        this.paused = this.$refs.video.paused;
                    });

                    hls.loadSource(hlsSource);
                    hls.attachMedia(this.$refs.video);
                })
                .catch((error) => {
                    // allow the import to be retried, as a native load() would
                    hlsjs = null;
                    throw error;
                });

            return hlsjs;
        },

        /**
         * Then load video segment on demand
         */
        preloadHls() {
            if (!hls) return;
            if (hls.loadingEnabled) return;

            hls.startLoad();
        },

        canPlayHls() {
            return this.$refs.video.canPlayType("application/vnd.apple.mpegurl");
        },

        withHls(callback) {
            if (this.isHls() && !this.canPlayHls()) {
                return this.attachHls().then(callback);
            }

            return callback();
        },

        video: {
            ["x-on:click"]() {
                if (
                    fullscreenOnTouch &&
                    window.matchMedia("(hover: none) and (pointer: coarse)").matches
                ) {
                    // Fullscreen hides the player controls, so the video plays with sound
                    this.unmute();
                    this.play();
                    return this.requestFullscreen();
                }

                this.toggle();
            },
            ["x-on:dblclick"]() {
                this.toggleFullScreen();
            },
            ["x-on:play"]() {
                this.preloadHls();
                this.paused = false;
            },
            ["x-on:pause"]() {
                this.paused = true;

                if (!this.isSeeking) {
                    this.currentTime = this.$refs.video.currentTime;
                }
            },
            ["x-on:timeupdate"]() {
                if (!this.isSeeking) {
                    this.currentTime = this.$refs.video.currentTime || 0;
                }
            },
            ["x-on:ended"]() {
                this.paused = true;

                if (!this.isSeeking) {
                    this.currentTime = this.$refs.video.currentTime || this.duration || 0;
                }
            },
            ["x-on:progress"]() {
                this.updateBufferedTime();
            },
            ["x-on:seeked"]() {
                this.updateBufferedTime();
            },
            ["x-on:emptied"]() {
                this.updateBufferedTime();
            },
            ["x-on:durationchange"]() {
                const duration = this.$refs.video.duration;

                if (Number.isFinite(duration) && duration > 0) {
                    this.duration = duration;
                }
            },
            ["x-intersect.once.margin.200px"]() {
                const video = this.$refs.video;

                // do not use this.$refs.video.loading as it is not supported in all browsers
                if (video.getAttribute("loading") === "lazy") {
                    return this.withHls(() => {
                        video.preload = "auto";
                        this.preloadHls();
                    });
                }

                if (video.preload !== "none") {
                    return this.withHls(() => this.preloadHls());
                }
            },
            ["x-bind:data-paused"]() {
                return this.paused ? "" : null;
            },
            ["x-bind:data-time"]() {
                return Math.floor(this.currentTime * 1_000) / 1_000;
            },
            ["x-bind:data-duration"]() {
                return Math.floor(this.duration * 1_000) / 1_000;
            },
            ["x-bind:data-progress"]() {
                return Math.floor(this.progress * 100);
            },
            ["x-bind:data-buffered"]() {
                return Math.floor(this.bufferedTime * 1_000) / 1_000;
            },
        },
    };
};
